<template src="./keyword-dedup-canonical-detail.component.html"></template>
<style scoped src="./keyword-dedup-canonical-detail.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import api from '@/api';
import {getErrorMessage} from '@/helpers';
import {toasted} from '@/plugins/toasted';
import type {KeywordDedupCanonicalRow, KeywordDedupCluster} from '@/models';

type KeywordDedupCanonicalResponse = {
  cluster?: unknown
  rows?: unknown
  count?: unknown
  total?: unknown
  data?: unknown
  keywords?: unknown
  members?: unknown
};

function getQueryParamValue(query: Record<string, any>, key: string) {
  const value = query[key];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : '';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function asNullableNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function asOptionalString(value: unknown) {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized === '' ? null : normalized;
}

function normalizeMarket(value: unknown, fallback: string = '') {
  return String(value ?? fallback).trim().toUpperCase();
}

function normalizeKeywordMatchValue(value: unknown) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function capitalizeFirstLetter(value: unknown) {
  const normalized = String(value ?? '').trim();
  if (normalized.length === 0) return null;
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}

function buildFallbackCluster(
  clusterId: string,
  canonicalKeyword: string,
  market: string,
  keywordCount: number,
): KeywordDedupCluster | null {
  const id = asNullableNumber(clusterId);
  if (id === null && !canonicalKeyword && !market && keywordCount === 0) return null;
  return {
    id: id ?? 0,
    market: normalizeMarket(market),
    canonical_keyword: canonicalKeyword,
    query_type: null,
    base_entity: null,
    cluster_confidence: null,
    keyword_count: keywordCount,
    status: '',
    created_at: null,
    updated_at: null,
  };
}

function normalizeCluster(
  value: unknown,
  clusterId: string,
  fallbackCanonicalKeyword: string,
  fallbackMarket: string,
  fallbackCount: number,
): KeywordDedupCluster | null {
  if (!isPlainObject(value)) {
    return buildFallbackCluster(clusterId, fallbackCanonicalKeyword, fallbackMarket, fallbackCount);
  }

  const id = asNullableNumber(value.id) ?? asNullableNumber(clusterId) ?? 0;
  const market = normalizeMarket(value.market, fallbackMarket);
  const canonicalKeyword = asOptionalString(value.canonical_keyword) ?? fallbackCanonicalKeyword;
  const keywordCount = asNullableNumber(value.keyword_count) ?? fallbackCount;

  return {
    id,
    market,
    canonical_keyword: canonicalKeyword,
    query_type: capitalizeFirstLetter(value.query_type),
    base_entity: asOptionalString(value.base_entity),
    cluster_confidence: asNullableNumber(value.cluster_confidence),
    keyword_count: keywordCount,
    status: asOptionalString(value.status) ?? '',
    created_at: asOptionalString(value.created_at),
    updated_at: asOptionalString(value.updated_at),
  };
}

function normalizeRow(
  value: unknown,
  fallbackClusterId: string,
  fallbackCanonicalKeyword: string,
  fallbackMarket: string,
): KeywordDedupCanonicalRow | null {
  if (!isPlainObject(value)) return null;

  const keyword = asOptionalString(value.keyword);
  if (!keyword) return null;

  return {
    id: asNullableNumber(value.id),
    keyword,
    market: normalizeMarket(value.market, fallbackMarket),
    vertical: asOptionalString(value.vertical),
    query_type: capitalizeFirstLetter(value.query_type),
    recommended_campaign_keyword:
      asOptionalString(value.recommended_campaign_keyword) ??
      asOptionalString(value.canonical_keyword) ??
      fallbackCanonicalKeyword,
    cluster_id: asNullableNumber(value.cluster_id) ?? asNullableNumber(fallbackClusterId),
    decision: asOptionalString(value.decision) ?? '',
    created_at: asOptionalString(value.created_at),
  };
}

function extractRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const candidates = [
    payload.rows,
    payload.keywords,
    payload.members,
    payload.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  if (isPlainObject(payload.cluster)) {
    const clusterCandidates = [payload.cluster.rows, payload.cluster.keywords, payload.cluster.members];
    for (const candidate of clusterCandidates) {
      if (Array.isArray(candidate)) return candidate;
    }
  }

  return [];
}

function extractCluster(payload: unknown): unknown {
  if (!isPlainObject(payload)) return null;
  if (isPlainObject(payload.cluster)) return payload.cluster;
  if (Object.prototype.hasOwnProperty.call(payload, 'canonical_keyword')) return payload;
  return null;
}

function extractCount(payload: unknown, rowsLength: number, cluster: KeywordDedupCluster | null) {
  if (isPlainObject(payload)) {
    const count = asNullableNumber(payload.count) ?? asNullableNumber(payload.total);
    if (count !== null) return count;
  }
  if (cluster) return cluster.keyword_count;
  return rowsLength;
}

function omitHelperQueryParams(query: Record<string, any>) {
  const result: Record<string, string> = {};
  const helperKeys = new Set(['canonical', 'sourceMarket', 'returnClusterId']);
  for (const [key, value] of Object.entries(query)) {
    if (helperKeys.has(key)) continue;
    if (Array.isArray(value)) {
      if (value[0] !== undefined) result[key] = String(value[0]);
      continue;
    }
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  }
  return result;
}

const CLUSTER_ENDPOINT_CANDIDATES = [
  (clusterId: string) => `/keyword-dedup/clusters/${encodeURIComponent(clusterId)}/keywords`,
  (clusterId: string) => `/keyword-dedup/keywords?clusterId=${encodeURIComponent(clusterId)}&page=1&perPage=5000&column=keyword&direction=asc`,
  (clusterId: string) => `/keyword-dedup/clusters/${encodeURIComponent(clusterId)}`,
];

const EXACT_MATCH_PER_PAGE = 500;

export default Vue.extend({
  components: {
    Sidebar,
  },
  data() {
    return {
      ac: null as AbortController | null,
      rows: [] as KeywordDedupCanonicalRow[],
      cluster: null as KeywordDedupCluster | null,
      showLoading: false,
      tableError: '',
      fallbackCanonicalKeyword: '',
      fallbackMarket: '',
    };
  },
  computed: {
    clusterId(): string {
      return String(this.$route.params.clusterId ?? '').trim();
    },
    canonicalKeyword(): string {
      return this.cluster?.canonical_keyword || this.fallbackCanonicalKeyword || this.rows[0]?.recommended_campaign_keyword || '';
    },
    market(): string {
      return this.cluster?.market || this.fallbackMarket || this.rows[0]?.market || '';
    },
    exactCanonicalKeyword(): string {
      return this.fallbackCanonicalKeyword.trim();
    },
    exactCanonicalLookupEnabled(): boolean {
      return this.clusterId === 'canonical-match' && Boolean(this.exactCanonicalKeyword && this.fallbackMarket);
    },
    keywordCount(): number {
      return this.cluster?.keyword_count ?? this.rows.length;
    },
    pageHeading(): string {
      return this.canonicalKeyword
        ? `Keywords using ${this.canonicalKeyword}`
        : 'Canonical keyword cluster';
    },
    listQuery(): Record<string, string> {
      return omitHelperQueryParams(this.$route.query);
    },
    detailQuery(): Record<string, string> {
      const query = {...this.listQuery};
      if (this.canonicalKeyword) query.canonical = this.canonicalKeyword;
      if (this.market) query.sourceMarket = this.market;
      if (this.clusterId) query.returnClusterId = this.clusterId;
      return query;
    },
    isShowingPartialResults(): boolean {
      return this.keywordCount > this.rows.length;
    },
  },
  watch: {
    '$route.query': {
      handler(query: Record<string, any>) {
        this.fallbackCanonicalKeyword = getQueryParamValue(query, 'canonical').trim();
        this.fallbackMarket = normalizeMarket(getQueryParamValue(query, 'sourceMarket'));
      },
      immediate: true,
    },
    '$route.params.clusterId': {
      async handler() {
        await this.getClusterKeywords();
      },
      immediate: true,
    },
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
    this.ac?.abort();
  },
  methods: {
    async getClusterKeywords() {
      const clusterId = this.clusterId;
      if (!clusterId) {
        this.cluster = null;
        this.rows = [];
        this.tableError = 'Missing cluster id.';
        return;
      }

      this.showLoading = true;
      this.tableError = '';
      this.rows = [];
      this.cluster = null;
      this.ac?.abort();
      this.ac = new AbortController();

      if (this.exactCanonicalLookupEnabled) {
        await this.getExactCanonicalKeywords(clusterId);
        return;
      }

      let lastError: unknown = null;

      try {
        for (const getResource of CLUSTER_ENDPOINT_CANDIDATES) {
          const resource = getResource(clusterId);

          try {
            const response = await api.execute(
              'get',
              resource,
              null,
              null,
              this.ac.signal,
            ) as KeywordDedupCanonicalResponse | unknown[];

            const rows = extractRows(response)
              .map(row => normalizeRow(
                row,
                clusterId,
                this.fallbackCanonicalKeyword,
                this.fallbackMarket,
              ))
              .filter((row): row is KeywordDedupCanonicalRow => row !== null);

            const normalizedCluster = normalizeCluster(
              extractCluster(response),
              clusterId,
              this.fallbackCanonicalKeyword,
              this.fallbackMarket,
              rows.length,
            );

            const count = extractCount(response, rows.length, normalizedCluster);

            this.rows = rows;
            this.cluster = normalizeCluster(
              normalizedCluster,
              clusterId,
              this.fallbackCanonicalKeyword,
              this.fallbackMarket,
              count,
            );

            return;
          } catch (e) {
            if (axios.isCancel(e)) return;
            lastError = e;
            const status = axios.isAxiosError(e) ? e.response?.status : undefined;
            if (status === 400 || status === 404) continue;
          }
        }

        throw lastError ?? new Error('Unable to load canonical keyword cluster.');
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.rows = [];
          this.cluster = buildFallbackCluster(
            clusterId,
            this.fallbackCanonicalKeyword,
            this.fallbackMarket,
            0,
          );
          this.tableError = getErrorMessage(e);
          toasted.showErrorMessage(this.tableError);
        }
      } finally {
        this.showLoading = false;
      }
    },
    async getExactCanonicalKeywords(clusterId: string) {
      try {
        const normalizedCanonical = normalizeKeywordMatchValue(this.exactCanonicalKeyword);
        const market = this.fallbackMarket;
        const rows: KeywordDedupCanonicalRow[] = [];

        let page = 1;
        let total = 0;

        do {
          const searchParams = new URLSearchParams();
          searchParams.append('page', String(page));
          searchParams.append('perPage', String(EXACT_MATCH_PER_PAGE));
          searchParams.append('market', market);
          searchParams.append('column', 'keyword');
          searchParams.append('direction', 'asc');

          const response = await api.execute(
            'get',
            `/keyword-dedup/keywords?${searchParams.toString()}`,
            null,
            null,
            this.ac?.signal,
          ) as KeywordDedupCanonicalResponse;

          const pageRows = extractRows(response)
            .map(row => normalizeRow(
              row,
              clusterId,
              this.exactCanonicalKeyword,
              market,
            ))
            .filter((row): row is KeywordDedupCanonicalRow => row !== null)
            .filter(row => normalizeKeywordMatchValue(row.recommended_campaign_keyword) === normalizedCanonical);

          rows.push(...pageRows);

          const count = extractCount(response, pageRows.length, null);
          total = Number.isFinite(count) ? count : rows.length;
          page += 1;
        } while ((page - 1) * EXACT_MATCH_PER_PAGE < total);

        this.rows = rows;
        this.cluster = {
          id: asNullableNumber(clusterId) ?? 0,
          market,
          canonical_keyword: this.exactCanonicalKeyword,
          query_type: rows[0]?.query_type ?? null,
          base_entity: null,
          cluster_confidence: null,
          keyword_count: rows.length,
          status: '',
          created_at: null,
          updated_at: null,
        };
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.rows = [];
          this.cluster = buildFallbackCluster(
            clusterId,
            this.exactCanonicalKeyword,
            this.fallbackMarket,
            0,
          );
          this.tableError = getErrorMessage(e);
          toasted.showErrorMessage(this.tableError);
        }
      } finally {
        this.showLoading = false;
      }
    },
    getRowKey(row: KeywordDedupCanonicalRow, index: number) {
      return row.id ?? `${row.market}-${row.keyword}-${index}`;
    },
    getMarketFlag(market: string) {
      const normalized = String(market ?? '').trim().toUpperCase();
      const flagCode = normalized === 'UK' ? 'GB' : normalized;
      if (!/^[A-Z]{2}$/.test(flagCode)) return '';
      const codePoints = Array.from(flagCode).map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    },
  },
});
</script>
